import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="text-2xl font-bold mb-4">
              <span className="text-teal-400">Form</span>
              <span className="text-teal-200">Forge</span>
              <span className="text-teal-400">Pro</span>
            </div>
            <p className="text-gray-300 mb-4 max-w-md">
              Simplifying legal document preparation with guided, step-by-step forms. 
              Get the legal documents you need without the complexity or high costs.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.facebook.com/formforgepro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-200"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://x.com/formforgepro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-200"
                aria-label="Follow us on X (Twitter)"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/formforgepro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-200"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.tiktok.com/@formforgepro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-200"
                aria-label="Follow us on TikTok"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.849-1.374-1.888-1.374-3.089V0h-3.12v16.224c0 .211-.054.416-.127.608a2.718 2.718 0 0 1-2.59 1.878c-1.492 0-2.719-1.227-2.719-2.72s1.227-2.719 2.72-2.719c.283 0 .556.043.81.128v-3.218a5.8 5.8 0 0 0-.81-.057 5.923 5.923 0 0 0-5.924 5.924c0 3.272 2.652 5.924 5.924 5.924a5.923 5.923 0 0 0 5.924-5.924V8.539a9.234 9.234 0 0 0 5.352 1.706V7.112a6.191 6.191 0 0 1-2.486-1.55z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/formforgepro" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-teal-400 transition-colors duration-200"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/services" className="text-gray-300 hover:text-teal-400 transition-colors">Services</Link></li>
              <li><Link to="/how-it-works" className="text-gray-300 hover:text-teal-400 transition-colors">How It Works</Link></li>
              <li><Link to="/pricing" className="text-gray-300 hover:text-teal-400 transition-colors">Pricing</Link></li>
              <li><Link to="/help" className="text-gray-300 hover:text-teal-400 transition-colors">Help & FAQ</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-teal-400 transition-colors">About Us</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Contact</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="h-4 w-4 text-teal-400" />
                <span className="text-gray-300">support@formforgepro.com</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © 2026 FormForgePro. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/legal-disclaimer" className="text-gray-400 hover:text-teal-400 text-sm transition-colors">
              Legal Disclaimer
            </Link>
            <Link to="/privacy-policy" className="text-gray-400 hover:text-teal-400 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-teal-400 text-sm transition-colors">
              Refund Policy
            </Link>
            <Link to="/terms-of-service" className="text-gray-400 hover:text-teal-400 text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>

        {/* Legal Notice */}
        <div className="mt-6">
          <p className="text-xs text-gray-400 text-center md:whitespace-nowrap">
            <strong>IMPORTANT LEGAL NOTICE:</strong> FormForgePro provides legal document preparation services and guidance only. We are not attorneys and do not provide legal advice. Our services are not a substitute for the advice of an attorney.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;